import {
  convertToTimelineFlight,
  convertToTimelineGroundEvent,
  IAircraft,
  IAircraftEvents,
  IArrivalMessagePayload,
  IBulkEventPayload,
  IDepartureMessagePayload,
  IDynamicQuery,
  IFlight,
  IFlightTemplate,
  IFullFlight,
  IGroundEvent,
  ISwapAircraftEventData,
  unassignedAircraftId,
} from "@vmosolutions/oc-interfaces";
import { API, Auth } from "aws-amplify";
import _ from "lodash";
import moment from "moment-timezone";
import { AnyAction } from "redux";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { checkIntervalBlockIsLoaded } from "src/pages/FlightTimeline/FlightTimeline";
import { IRootState } from "../../reducers/CombinedReducers";
import { IAircraftFlights, isFlight, TimelineEvent } from "../../reducers/FlightTimelineReducer";
import { openSnackbar } from "../App.Actions";
import { formatBulkActionTypeSnackbarMessage } from "../flights/BulkOperations.Actions";
import { setCrewConnections, setLoadedDataInterval, Type } from "./FlightTimeline.Actions";

/*
export interface IUpdateSelectedFlight {
  type: Type.UPDATE_SELECTED_FLIGHT;
  flightId: string;
  flightData: any;
}
*/

export interface IGotUpdatedFlight {
  type: Type.TIMELINE_GOT_UPDATED_FLIGHT;
  flightData: any;
  oldEvents?: (IGroundEvent | IFlight)[];
  newEvents?: (IGroundEvent | IFlight)[];
}

export interface IGetUnassignedAircraftFlights {
  type: Type.TIMELINE_GET_UNASSIGNED_AIRCRAFT_FLIGHTS;
  unassignedFlights: IFlight[];
}

export interface IBuiltRecurringFlights {
  templateFile: IFlightTemplate;
  startDate: Date;
  until: Date;
  tempIds?: string[];
}

const sortFlightData = (flightData: IAircraftFlights, updatedAircrafts: string[]): void => {
  updatedAircrafts.forEach((aircraftId: string) => {
    flightData[aircraftId].flights.sort((a: IFlight, b: IFlight) => {
      return new Date(a.actualOff).getTime() - new Date(b.actualOff).getTime();
    });
    flightData[aircraftId].groundEvents.sort((a: IGroundEvent, b: IGroundEvent) => {
      return new Date(a.actualStart).getTime() - new Date(b.actualStart).getTime();
    });
  });
};

const parseBasicFlights = (basicEvents: TimelineEvent[], getState: () => IRootState): IAircraftFlights => {
  const aircraftFlightData = { ...getState().timelineFlights.flightData };
  const updatedAircrafts: Set<string> = new Set<string>();
  const lastProcessedTime = Date.now();
  Object.values(basicEvents).forEach((event: TimelineEvent, index: number) => {
    //don't add to flight data if a removed flight
    if (isFlight(event) && !!event.removalType) {
      return; //go to next item in the array
    }
    // Update aircraft or unassigned collection.
    const aircraftId = event?.aircraft?._id ?? "Unassigned";
    // If aircraft or unassigned collection don't exist, add it.
    if (!aircraftFlightData[aircraftId]) {
      aircraftFlightData[aircraftId] = {
        flights: [],
        groundEvents: [],
        lastProcessedTime,
      };
    }
    const eventType = isFlight(event) ? "flights" : "groundEvents";
    const eventIndex = aircraftFlightData[aircraftId][eventType].findIndex(
      (loadedEvent: any) => loadedEvent?._id === event?._id
    );
    if (eventIndex === -1) {
      // console.log(`Add Aircraft(${aircraftId} ${eventType} event:`, event);
      aircraftFlightData[aircraftId][eventType].push(event as any); // ADD
    } else {
      // console.log(`Update Aircraft(${aircraftId} ${eventType} event:`, event);
      aircraftFlightData[aircraftId][eventType][eventIndex] = event; // UPDATE
    }
    // Update last processedTime
    aircraftFlightData[aircraftId].lastProcessedTime = lastProcessedTime;
    //console.log(aircraftId, aircraftFlightData[aircraftId][eventType]);
    updatedAircrafts.add(aircraftId);
  });
  sortFlightData(aircraftFlightData, Array.from(updatedAircrafts));
  return aircraftFlightData;
};

const parseAircraftFlights = (data: any[], getState: () => IRootState): IAircraftFlights => {
  const aircraftFlightData = { ...getState().timelineFlights.flightData };
  const lastProcessedTime = Date.now();
  data.forEach((aircraftGroup: any, index: number) => {
    const aircraftId = aircraftGroup._id ?? unassignedAircraftId;
    if (!aircraftFlightData[aircraftId]) {
      aircraftFlightData[aircraftId] = {
        flights: [],
        groundEvents: [],
        lastProcessedTime,
      };
    }

    // FLIGHTS
    const flights = [...aircraftFlightData[aircraftId].flights, ...aircraftGroup.flights];
    // Sort Flights before keeping unique so that only the latest modifiedBy version is kept
    const sortedFlights = flights.sort((a, b) => (b?.modifiedAt > a?.modifiedAt ? 1 : -1));
    const uniqueFlights = _.uniqBy(sortedFlights, _.iteratee("_id"));
    uniqueFlights.sort((a: any, b: any) => a.actualOff - b.actualOff);

    // GROUND EVENTS
    const groundEvents: IGroundEvent[] = [
      ...aircraftFlightData[aircraftId].groundEvents,
      ...aircraftGroup.groundEvents,
    ];
    // Sort Events before keeping unique so that only the latest modifiedBy version is kept
    const sortedGroundEvents = groundEvents.sort((a, b) => (b?.modifiedAt > a?.modifiedAt ? 1 : -1));
    const uniqueGroundEvents = _.uniqBy(sortedGroundEvents, _.iteratee("_id"));
    uniqueGroundEvents.sort(
      (a: IGroundEvent, b: IGroundEvent) => new Date(a.actualStart).getTime() - new Date(b.actualStart).getTime()
    );

    aircraftFlightData[aircraftId].flights = [...uniqueFlights];
    aircraftFlightData[aircraftId].groundEvents = [...uniqueGroundEvents];
    aircraftFlightData[aircraftId].lastProcessedTime = lastProcessedTime;
  });
  // console.log(aircraftFlightData);
  return aircraftFlightData;
};

const getAircraftFlightData = async (
  token: string,
  dispatch: ThunkDispatch<object, object, AnyAction>,
  getState: () => IRootState,
  startDate: Date,
  endDate: Date
): Promise<void> => {
  const data: IAircraftEvents[] = await API.get(
    "skytrains",
    `/flights?startTime=${startDate.getTime()}&endTime=${endDate.getTime()}`,
    {
      // having the below object included is for cognito auth on the API Gateway
      // if using AWS_IAM auth, this will error.
      headers: {
        Accept: "*/*",
        Authorization: token,
        // ContentEncoding: 'gzip'
      },
    }
  );
  const flightData = parseAircraftFlights(data, getState);
  dispatch({
    type: Type.TIMELINE_ADD_FLIGHT_DATA,
    flightData,
  });
};

interface ILocalTimeObject {
  day: number;
  hour: number;
  minute: number;
}

/**
 * Function used to return local time objects which are required in the timeline events.
 * @param time - time in epoch format
 * @param timezoneId - timezone id to convert time before retrieving integers for day/hour/min
 * @returns object containing three integers required
 */
const createLocalTimeObject = (time: number, timezoneId: string): ILocalTimeObject => {
  const LocalTimeObject: ILocalTimeObject = moment(time)
    .tz(timezoneId)
    .format("DD/HH/mm")
    .split("/")
    .reduce(
      (a, string, index) => ({ ...a, [index === 0 ? "day" : index === 1 ? "hour" : "minute"]: parseInt(string) }),
      { day: undefined, hour: undefined, minute: undefined }
    );
  return LocalTimeObject;
};

/**
 * Function used to pass in a full flight into the redux timeline data in case one is opened in an editor without the timeline being aware of it's existance.
 * @param event - the full flight or ground event recently opened in an editor.
 * @param type - type of even getting passed into the timeline data.
 * @param dispatch - dispatch used to call proper reducer
 * @param getState - current state of the timeline.
 */
export const addEventToTimeline = (
  event: IFullFlight | IGroundEvent,
  type: "flight" | "event",
  dispatch: ThunkDispatch<object, object, AnyAction>,
  getState: () => IRootState
): void => {
  const timelineEvent =
    type === "flight"
      ? convertToTimelineFlight(event as IFullFlight)
      : type === "event"
        ? convertToTimelineGroundEvent(event as IGroundEvent)
        : null;

  const aircraftId = event?.aircraft?._id ?? unassignedAircraftId;
  const aircraftFlightData = getState().timelineFlights.flightData;
  const foundEvent =
    type === "flight"
      ? aircraftFlightData?.[aircraftId]?.flights?.find((flight) => flight._id === timelineEvent?._id)
      : type === "event"
        ? aircraftFlightData?.[aircraftId]?.groundEvents?.find((flight) => flight._id === timelineEvent?._id)
        : null;

  if (foundEvent) {
    //! If the event is already found in redux, return without modifying anything.
    return;
  } else {
    if (type === "flight") {
      const flight = event as IFlight;
      const airportOptions = getState().structures.airportOptions;
      const originAirportInfo = airportOptions?.[flight?.origin];
      const destinationAirportInfo = airportOptions?.[flight?.destination];

      if (!originAirportInfo || !destinationAirportInfo) return;

      // Origin Local Info
      const scheduledOutLocal = createLocalTimeObject(flight?.scheduledOut?.valueOf(), originAirportInfo?.timezoneId);
      const actualOutLocal = createLocalTimeObject(flight?.actualOut?.valueOf(), originAirportInfo?.timezoneId);
      const originAirport = { IATACode: originAirportInfo?.iata, timezoneId: originAirportInfo?.timezoneId };
      // Destination Local Info
      const scheduledInLocal = createLocalTimeObject(
        flight?.scheduledIn?.valueOf(),
        destinationAirportInfo?.timezoneId
      );
      const actualInLocal = createLocalTimeObject(flight?.actualIn?.valueOf(), destinationAirportInfo?.timezoneId);
      const destAirport = { IATACode: destinationAirportInfo?.iata, timezoneId: destinationAirportInfo?.timezoneId };
      // Add required fields to timeline flight
      const timelineFlightWithLocal = {
        ...flight,
        scheduledOutLocal,
        actualOutLocal,
        originAirport,
        scheduledInLocal,
        actualInLocal,
        destAirport,
      };
      const data = [
        {
          _id: aircraftId, // The aircraft you wish you pass these events to, if null will be assigned to unassignedAircraft
          flights: [timelineFlightWithLocal],
          groundEvents: [] as IGroundEvent[],
        },
      ];
      // Add to existing data (this should always null if this function is called but just to be sure pass it in.)
      const flightData = parseAircraftFlights(data, getState);
      dispatch({
        type: Type.TIMELINE_ADD_FLIGHT_DATA,
        flightData,
      });
    } else if (type === "event") {
      const groundEvent = event as IGroundEvent;
      const airportOptions = getState().structures.airportOptions;
      const locationAirportInfo = airportOptions?.[groundEvent?.location];

      if (!locationAirportInfo) return;

      // Start Local Info
      const scheduledStartLocal = createLocalTimeObject(
        groundEvent?.scheduledStart?.valueOf(),
        locationAirportInfo?.timezoneId
      );
      const actualStartLocal = createLocalTimeObject(
        groundEvent?.actualStart?.valueOf(),
        locationAirportInfo?.timezoneId
      );
      // End Local Info
      const scheduledEndLocal = createLocalTimeObject(
        groundEvent?.scheduledEnd?.valueOf(),
        locationAirportInfo?.timezoneId
      );
      const actualEndLocal = createLocalTimeObject(groundEvent?.actualEnd?.valueOf(), locationAirportInfo?.timezoneId);
      const locationAirport = { IATACode: locationAirportInfo?.iata, timezoneId: locationAirportInfo?.timezoneId };
      // Add required fields to timeline event
      const timelineEventWithLocal = {
        ...groundEvent,
        scheduledStartLocal,
        actualStartLocal,
        scheduledEndLocal,
        actualEndLocal,
        locationAirport,
      };
      const data = [
        {
          _id: aircraftId, // The aircraft you wish you pass these events to, if null will be assigned to unassignedAircraft
          flights: [] as IFlight[],
          groundEvents: [timelineEventWithLocal],
        },
      ];
      // Add to existing data (this should always null if this function is called but just to be sure pass it in.)
      const flightData = parseAircraftFlights(data, getState);
      dispatch({
        type: Type.TIMELINE_ADD_FLIGHT_DATA,
        flightData,
      });
    }
  }
};

export const retrieveFlightData = (
  beforeDate: Date,
  afterDate: Date,
  numberDaysForChunk?: number
): ThunkAction<Promise<any>, IRootState, object, AnyAction> => {
  return async (dispatch: ThunkDispatch<object, object, AnyAction>, getState: () => IRootState): Promise<any> => {
    const userSession = await Auth.currentSession();
    const token = userSession.getIdToken().getJwtToken();
    const intervalLoaded = getState().timelineFlights.timeIntervalLoaded;
    const numDaysForChunk =
      numberDaysForChunk ??
      getState().structures.customerStructure.globalAppConfig.customer.settings.defaultFlightTimelineLoadDays ??
      16;
    let flightDataPromises: Promise<void>[] = [];
    let numAircraftsLen = 0;
    //intial time check first month
    let batchStart = moment(beforeDate).startOf("month");
    let batchEnd = moment(beforeDate).endOf("month");
    const blockInterval = [];
    let dbQueryCount = 1;
    while (moment(afterDate).endOf("month") >= batchEnd) {
      const intervalLoadedCheck = checkIntervalBlockIsLoaded(intervalLoaded, batchStart);
      //if you are going to load data
      if (!intervalLoadedCheck.loaded) {
        //when scrolling quickly or zooming out you may have a few calls to this functions
        //a call may finish and set flightDataLoaded to true while another call is still in progress
        //maybe change has flight_data_loaded is stored
        dispatch({
          type: Type.TIMELINE_FLIGHT_DATA_LOADING,
          interval: intervalLoadedCheck.blockKey,
          loaded: false,
        });
        blockInterval.push(intervalLoadedCheck.blockKey);

        //if the interval is not loaded, update redux that you are loading it
        dispatch(
          setLoadedDataInterval({
            [intervalLoadedCheck.blockKey]: {
              startOfInterval: batchStart?.valueOf(),
              endOfInterval: batchEnd?.valueOf(),
            },
          })
        );

        let lastDateForBatch = moment(batchStart);
        while (batchStart < batchEnd) {
          lastDateForBatch.add(numDaysForChunk, "days");
          if (lastDateForBatch > batchEnd) {
            lastDateForBatch = batchEnd;
          }
          dbQueryCount++;
          flightDataPromises.push(
            getAircraftFlightData(token, dispatch, getState, batchStart.toDate(), lastDateForBatch.toDate())
          );
          batchStart.add(numDaysForChunk, "days");

          //dont have more than 4 of each query at a time on the db
          if (dbQueryCount >= 4) {
            //wait till they finish...
            await Promise.all(flightDataPromises);
            numAircraftsLen = numAircraftsLen + flightDataPromises.length;
            flightDataPromises = [];
            //reset count
            dbQueryCount = 0;
          }
        }
      }

      //check the next month block - reset batch start and end
      batchStart = batchEnd.add(1, "month").startOf("month");
      batchEnd = moment(batchStart).endOf("month"); //moment(beforeDate).add(monthIndex, "month").endOf("month");
    }

    dispatch({
      type: Type.TIMELINE_NUM_AIRCRAFTS,
      numAircrafts: numAircraftsLen + flightDataPromises.length,
    });
    await Promise.all(flightDataPromises);
    blockInterval.forEach((interval) => {
      dispatch({
        type: Type.TIMELINE_FLIGHT_DATA_LOADING,
        interval: interval,
        loaded: true,
      });
    });
  };
};

export const getUnassignedAircraftFlights = () => {
  return async (
    dispatch: ThunkDispatch<IRootState, object, IGetUnassignedAircraftFlights>,
    getState: () => IRootState
  ): Promise<any> => {
    const userSession = await Auth.currentSession();
    const token = userSession.getIdToken().getJwtToken();

    const unassignedFlights = await API.get("skytrains", "/unassignedFlights", {
      headers: {
        Accept: "*/*",
        Authorization: token,
      },
    });

    dispatch({
      type: Type.TIMELINE_GET_UNASSIGNED_AIRCRAFT_FLIGHTS,
      unassignedFlights,
    });
  };
};

/**
 * Create a new ground event (used in the flight builder tool)
 */
export const createGroundEvent = (
  groundEvent: any,
  displaySnackbar?: boolean
): ThunkAction<Promise<any>, object, object, AnyAction> => {
  return async (dispatch: ThunkDispatch<object, object, AnyAction>, getState: any): Promise<IGroundEvent | any> => {
    const userSession = await Auth.currentSession();
    const token = userSession.getIdToken().getJwtToken();

    dispatch(
      openSnackbar({
        open: true,
        closeDelay: null,
        variant: "info",
        message: "Adding Ground Event...",
      })
    );

    const response = await API.post("skytrains", "/groundEvents", {
      headers: {
        Accept: "*/*",
        Authorization: token,
      },
      body: groundEvent,
    });
    if (displaySnackbar !== false) {
      if (!!response && typeof response === "object" && response.hasOwnProperty("_id")) {
        // SUCCESS
        dispatch(
          openSnackbar({
            open: true,
            closeDelay: 3000,
            variant: "success",
            message: "Ground Event Successfully Added To Timeline.",
          })
        );
      } else {
        // FAILURE
        dispatch(
          openSnackbar({
            open: true,
            closeDelay: 3000,
            variant: "error",
            message: "Something Went Wrong!",
          })
        );
      }
    }
    return response; // replace with callback from websocket when setup.
  };
};

/**
 * Create a new flight event (used in the flight builder tool)
 */
// export const createFlightEvent = (
//   flightEvent: any,
//   displaySnackbar?: boolean
// ): ThunkAction<Promise<any>, {}, {}, AnyAction> => {
//   return async (dispatch: ThunkDispatch<{}, {}, AnyAction>, getState: any): Promise<IFlight | any> => {
//     const userSession = await Auth.currentSession();
//     const token = userSession.getIdToken().getJwtToken();

//     dispatch(
//       openSnackbar({
//         open: true,
//         closeDelay: null,
//         variant: "info",
//         message: "Adding Flight Event...",
//       })
//     );

//     try {
//       const response = await API.post("skytrains", "/flights", {
//         headers: {
//           Accept: "*/*",
//           Authorization: token,
//         },
//         body: flightEvent,
//       });
//       if (displaySnackbar !== false) {
//         if (!!response && typeof response === "object" && response.hasOwnProperty("_id")) {
//           // SUCCESS
//           dispatch(
//             openSnackbar({
//               open: true,
//               closeDelay: 3000,
//               variant: "success",
//               message: "Flight Successfully Added To Timeline.",
//             })
//           );
//         }
//       }
//       // Debug.log(response);
//       return response; // replace with callback from websocket when setup.
//     } catch (error) {
//       // Debug.log(error.response)
//       const errorString =
//         !!error.response && !!error.response.data && !!error.response.data.error ? error.response.data.error : null;
//       // Debug.log(errorString)
//       // FAILURE
//       dispatch(
//         openSnackbar({
//           open: true,
//           closeDelay: 3000,
//           variant: "error",
//           message: errorString ? errorString : "Something Went Wrong!",
//         })
//       );
//       return error;
//     }
//   };
// };

/**
 * Will retrieve a given flight id and if found, add it to the redux form object in the store.
 * @param flightId - requested flight id
 */
export const addSelectedFlight = (flightId: string): ThunkAction<Promise<string>, object, object, AnyAction> => {
  return async (dispatch: ThunkDispatch<object, object, AnyAction>, getState: any): Promise<any> => {
    const userSession = await Auth.currentSession();
    const token = userSession.getIdToken().getJwtToken();
    const flightData = await API.get("skytrains", `/flights/${flightId}`, {
      headers: {
        Accept: "*/*",
        Authorization: token,
      },
    });
    if (flightData?._id) {
      dispatch({ type: Type.TIMELINE_ADD_EDIT_FLIGHT, flightData });
    }
  };
};

export enum UpdateFlightAction {
  Basic,
  SendMovementMessage,
}

export const updateSelectedFlight = (
  flightId: string,
  flightData: Partial<IFullFlight>
): ThunkAction<Promise<string>, object, object, AnyAction> => {
  return async (dispatch: ThunkDispatch<object, object, AnyAction>, getState: any): Promise<any> => {
    dispatch(
      openSnackbar({
        open: true,
        closeDelay: null,
        variant: "info",
        message: "Updating Flight Info...",
      })
    );

    const userSession = await Auth.currentSession();
    const token = userSession.getIdToken().getJwtToken();

    try {
      const response = await API.put("skytrains", `/flights/${flightId}`, {
        headers: { Accept: "*/*", Authorization: token },
        body: flightData,
      });

      if (!!response && typeof response === "object" && response.hasOwnProperty("newEvent")) {
        // SUCCESS
        dispatch(
          openSnackbar({
            open: true,
            closeDelay: 3000,
            variant: "success",
            message: "Flight Successfully Updated!",
          })
        );
      }

      return response;
    } catch (error) {
      // FAILURE
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: 3000,
          variant: "error",
          //@ts-ignore
          message: error?.response?.data?.error ?? "Something Went Wrong!",
        })
      );
      //@ts-ignore
      return error?.response;
    }
  };
};

export const postArrivalOrDepartureMessage = (
  flightId: string,
  msg: IArrivalMessagePayload | IDepartureMessagePayload
): ThunkAction<Promise<string>, object, object, AnyAction> => {
  return async (dispatch: ThunkDispatch<object, object, AnyAction>, getState: any): Promise<any> => {
    dispatch(
      openSnackbar({
        open: true,
        closeDelay: null,
        variant: "info",
        message: "Submitting Movement Message...",
      })
    );

    const userSession = await Auth.currentSession();
    const token = userSession.getIdToken().getJwtToken();

    try {
      const depArrMsg = (msg as IDepartureMessagePayload).departureMessage
        ? "dep"
        : (msg as IArrivalMessagePayload).arrivalMessage
          ? "arr"
          : null;

      let response: any = null;
      if (depArrMsg) {
        response = await API.post("skytrains", `/flight/postmessage/${depArrMsg}/${flightId}`, {
          headers: { Accept: "*/*", Authorization: token },
          body: msg,
        });

        if (!!response && typeof response === "object" && response.success === true) {
          // SUCCESS
          dispatch(
            openSnackbar({
              open: true,
              closeDelay: 3000,
              variant: "success",
              message: `${depArrMsg === "dep" ? "Departure" : "Arrival"} message submitted successfully.`,
            })
          );
        }
      } else throw new Error("Could not detect if message was a departure or arrival message.");

      return response;
    } catch (error) {
      // FAILURE
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: 3000,
          variant: "error",
          message: "Something Went Wrong!",
        })
      );
      //@ts-ignore
      return error?.response;
    }
  };
};

export const deleteFlight = (flightId: string): ThunkAction<Promise<string>, object, object, AnyAction> => {
  return async (dispatch: ThunkDispatch<object, object, AnyAction>, getState: any): Promise<any> => {
    const userSession = await Auth.currentSession();
    const token = userSession.getIdToken().getJwtToken();

    dispatch(
      openSnackbar({
        open: true,
        closeDelay: null,
        variant: "info",
        message: "Deleting flight...",
      })
    );

    const response = await API.del("skytrains", `/flights/${flightId}`, {
      headers: { Accept: "*/*", Authorization: token },
    });

    if (!!response && typeof response === "object" && response.hasOwnProperty("_id")) {
      // SUCCESS
      // dispatch({ type: Type.UPDATE_SELECTED_FLIGHT, flightId, flightData });
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: 3000,
          variant: "success",
          message: "Flight Successfully Updated!",
        })
      );
    } else {
      // FAILURE
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: 3000,
          variant: "error",
          message: "Something Went Wrong!",
        })
      );
    }
    return response;
  };
};

/**
 * Function used to re-assign or unassign flight events from a particular aircraft.
 * @param flightId -
 * @param aircraftData -
 */
export const updateAircraftFlightEvents = (flightId: string, oldAircraft: string, aircraftData: IAircraft) => {
  return async (dispatch: ThunkDispatch<object, object, AnyAction>, getState: any): Promise<any> => {
    const userSession = await Auth.currentSession();
    const token = userSession.getIdToken().getJwtToken();

    dispatch(
      openSnackbar({
        open: true,
        closeDelay: null,
        variant: "info",
        message: "Updating Aircraft...",
      })
    );

    const response = await API.put("skytrains", `/flights/${flightId}/aircraft`, {
      headers: {
        Accept: "*/*",
        Authorization: token,
      },
      body: aircraftData,
    });
    if (!!response && typeof response === "object" && response.hasOwnProperty("_id")) {
      // SUCCESS
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: 3000,
          variant: "success",
          message: "Aircraft Successfully Updated.",
        })
      );
      // update unassigned aircraft if assignments have changed.
      getUnassignedAircraftFlights();
    } else {
      // FAILURE
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: 3000,
          variant: "error",
          message: "Something Went Wrong!",
        })
      );
    }
    return response;
  };
};

/**
 * cancelFlight - moves the flight from the flights col to canceledflights col in DB
 * @param flightid - array containing relevent data gathered in the modal
 * @returns flightobject from the database
 */
//Do not use this one any more because cancel flight is a bulk action
// export const cancelFlight = (flightId: string, data: object): ThunkAction<Promise<string>, {}, {}, AnyAction> => {
//   return async (dispatch: ThunkDispatch<{}, {}, AnyAction>, getState: any): Promise<any> => {
//     const userSession = await Auth.currentSession();
//     const token = userSession.getIdToken().getJwtToken();

//     dispatch(
//       openSnackbar({
//         open: true,
//         closeDelay: null,
//         variant: "info",
//         message: "canceling flight...",
//       })
//     );

//     //add current user to data
//     const additionalData = {
//       canceled: true,
//       canceledBy: userSession.getIdToken().payload.sub,
//       canceledAt: new Date(),
//     };

//     const finalData = { ...data, ...additionalData };
//     const response = await API.put("skytrains", `/flights/${flightId}/cancel`, {
//       headers: { Accept: "*/*", Authorization: token },
//       body: finalData,
//     });

//     if (!!response && typeof response === "object" && response.hasOwnProperty("_id")) {
//       // SUCCESS
//       dispatch(
//         openSnackbar({
//           open: true,
//           closeDelay: 3000,
//           variant: "success",
//           message: "Flight Successfully Canceled!",
//         })
//       );
//     } else {
//       // FAILURE
//       dispatch(
//         openSnackbar({
//           open: true,
//           closeDelay: 3000,
//           variant: "error",
//           message: "Something Went Wrong!",
//         })
//       );
//     }
//     return response;
//   };
// };

// export const updateSelectedFlightAircraft = (flightId: string, aircraftData: IAircraft) => {
//   return async (dispatch: ThunkDispatch<{}, {}, AnyAction>, getState: any): Promise<any> => {
//     const userSession = await Auth.currentSession();
//     const token = userSession.getIdToken().getJwtToken();

//     await API.put("skytrains", `/flights/${flightId}/aircraft`, {
//       headers: {
//         Accept: "*/*",
//         Authorization: token,
//       },
//       body: aircraftData,
//     });
//     // dispatch({ type: Type.UPDATE_SELECTED_FLIGHT, flightId, flightData: data })
//   };
// };

//ground events

export const getSelectedGroundEvent = (
  groundEventId: string
): ThunkAction<Promise<IGroundEvent>, object, object, AnyAction> => {
  return async (dispatch: ThunkDispatch<object, object, AnyAction>, getState: any): Promise<IGroundEvent> => {
    const userSession = await Auth.currentSession();
    const token = userSession.getIdToken().getJwtToken();

    const response: IGroundEvent = await API.get("skytrains", `/groundEvents/${groundEventId}`, {
      headers: {
        Accept: "*/*",
        Authorization: token,
      },
    });

    if (!response?._id) {
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: 3000,
          variant: "error",
          message: "Unable to retrieve ground event!",
        })
      );
    }

    return response;
  };
};

export const updateSelectedGroundEvent = (
  groundEventId: string,
  groundEventData: Partial<IGroundEvent>
): ThunkAction<Promise<IGroundEvent>, object, object, AnyAction> => {
  return async (dispatch: ThunkDispatch<object, object, AnyAction>, getState: any): Promise<IGroundEvent> => {
    const userSession = await Auth.currentSession();
    const token = userSession.getIdToken().getJwtToken();

    dispatch(
      openSnackbar({
        open: true,
        closeDelay: null,
        variant: "info",
        message: "Updating ground event info...",
      })
    );

    const response = await API.put("skytrains", `/groundEvents/${groundEventId}`, {
      headers: { Accept: "*/*", Authorization: token },
      body: groundEventData,
    });
    if (!!response && typeof response === "object" && response.hasOwnProperty("_id")) {
      // SUCCESS
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: 3000,
          variant: "success",
          message: "Ground Event Successfully Updated!",
        })
      );
    } else {
      // FAILURE
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: 3000,
          variant: "error",
          message: "Something Went Wrong!",
        })
      );
    }

    return response;
  };
};

//replaced by the bulk delete/cancel action
// export const deleteSelectedGroundEvent = (groundEventId: string): ThunkAction<Promise<string>, {}, {}, AnyAction> => {
//   return async (dispatch: ThunkDispatch<{}, {}, AnyAction>, getState: any): Promise<any> => {
//     const userSession = await Auth.currentSession();
//     const token = userSession.getIdToken().getJwtToken();

//     dispatch(
//       openSnackbar({
//         open: true,
//         closeDelay: null,
//         variant: "info",
//         message: "Deleting ground event info...",
//       })
//     );

//     const response = await API.del("skytrains", `/groundEvents/${groundEventId}`, {
//       headers: { Accept: "*/*", Authorization: token },
//     });
//     if (!!response && typeof response === "object" && response.hasOwnProperty("_id")) {
//       // SUCCESS
//       dispatch(
//         openSnackbar({
//           open: true,
//           closeDelay: 3000,
//           variant: "success",
//           message: "Ground Event Successfully Deleted!",
//         })
//       );
//     } else {
//       // FAILURE
//       dispatch(
//         openSnackbar({
//           open: true,
//           closeDelay: 3000,
//           variant: "error",
//           message: "Something Went Wrong!",
//         })
//       );
//     }

//     return response;
//   };
// };

// export const cancelSelectedGroundEvent = (
//   groundEventId: string,
//   data: object
// ): ThunkAction<Promise<string>, {}, {}, AnyAction> => {
//   return async (dispatch: ThunkDispatch<{}, {}, AnyAction>, getState: any): Promise<any> => {
//     dispatch(
//       openSnackbar({
//         open: true,
//         closeDelay: null,
//         variant: "info",
//         message: "Canceling Ground Event...",
//       })
//     );

//     const userSession = await Auth.currentSession();
//     const token = userSession.getIdToken().getJwtToken();

//     //add current user to data
//     const additionalData = {
//       canceled: true,
//       canceledBy: userSession.getIdToken().payload.sub,
//       canceledAt: new Date(),
//     };

//     const finalData = { ...data, ...additionalData };

//     const response = await API.put("skytrains", `/groundEvents/${groundEventId}/cancel`, {
//       headers: { Accept: "*/*", Authorization: token },
//       body: finalData,
//     });
//     if (!!response && typeof response === "object" && response.hasOwnProperty("_id")) {
//       // SUCCESS
//       dispatch(
//         openSnackbar({
//           open: true,
//           closeDelay: 3000,
//           variant: "success",
//           message: "Ground Event Successfully Canceled!",
//         })
//       );
//     } else {
//       // FAILURE
//       dispatch(
//         openSnackbar({
//           open: true,
//           closeDelay: 3000,
//           variant: "error",
//           message: "Something Went Wrong!",
//         })
//       );
//     }

//     return response;
//   };
// };

export const swapAircraftEvents = (
  data: ISwapAircraftEventData
): ThunkAction<Promise<string>, object, object, AnyAction> => {
  return async (dispatch: ThunkDispatch<object, object, AnyAction>, getState: any): Promise<any> => {
    const userSession = await Auth.currentSession();
    const token = userSession.getIdToken().getJwtToken();

    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: "Swapping Aircraft...",
      successMessage: "Aircraft Events Successfully Swapped",
      errorMessage: "Something Went Wrong!",
    };

    const response = await AmplifyAPIWrapper.Post<any>("/flights/swapAircraftEvents", data, notifications);

    return response;
  };
};

///////////////// BULK RECURRING EVENTS  \\\\\\\\\\\\\\\\\\\\\\
// not sure we want this any more - no deployed endpoint for this
// export const bulkRecurringEvent = (payload: IBuiltRecurringFlights) => {
//   return async (dispatch: ThunkDispatch<{}, {}, AnyAction>, getState: any): Promise<string> => {
//     const userSession = await Auth.currentSession();
//     const token = userSession.getIdToken().getJwtToken();

//     dispatch(
//       openSnackbar({
//         open: true,
//         closeDelay: null,
//         variant: "info",
//         message: "Building Recurring Flights...",
//       })
//     );

//     const response = await API.post("skytrains", "/bulk/recurring", {
//       headers: {
//         Accept: "*/*",
//         Authorization: token,
//       },
//       body: payload,
//     });
//     if (!!response && typeof response === "object" && response.hasOwnProperty("success") && response.success) {
//       // SUCCESS
//       dispatch(
//         openSnackbar({
//           open: true,
//           closeDelay: 3000,
//           variant: "success",
//           message: `${response.numSQSResults} of ${response.numRecords} requested flights sent to be built`,
//         })
//       );
//     } else {
//       // FAILURE
//       dispatch(
//         openSnackbar({
//           open: true,
//           closeDelay: 3000,
//           variant: "error",
//           message: "Something Went Wrong!",
//         })
//       );
//       //return "ERROR";
//     }
//     return response?.batchId;
//     // Debug.log(response);
//     // dispatch({ type: Type.UPDATE_SELECTED_FLIGHT, flightId, flightData: data })
//   };
// };

///////////////// BULK OPTION \\\\\\\\\\\\\\\\\\\\\\

export const chunkPayload = <T>(payload: T[], size: number): T[][] => {
  return Array.from({ length: Math.ceil(payload.length / size) }, (v: any, i: number) => {
    return payload.slice(i * size, i * size + size);
  });
};

export const bulkEvent = (payload: IBulkEventPayload, numOfRecords: number) => {
  return async (dispatch: ThunkDispatch<object, object, AnyAction>, getState: any): Promise<any> => {
    const userSession = await Auth.currentSession();
    const token = userSession.getIdToken().getJwtToken();
    const snackBarMessage: string = formatBulkActionTypeSnackbarMessage(payload.actionType, numOfRecords);

    // dispatch(
    //   openSnackbar({
    //     open: true,
    //     closeDelay: null,
    //     variant: "info",
    //     message: `Sending ${requestType}Batch Request...`
    //   })
    // );
    try {
      const response = await API.post("skytrains", "/bulk", {
        headers: {
          Accept: "*/*",
          Authorization: token,
        },
        body: payload,
      });
      if (!!response && typeof response === "object" && response.hasOwnProperty("success") && response.success) {
        // SUCCESS
        dispatch(
          openSnackbar({
            open: true,
            closeDelay: 3000,
            variant: "success",
            message: snackBarMessage,
          })
        );
      } else {
        // FAILURE
        dispatch(
          openSnackbar({
            open: true,
            closeDelay: 3000,
            variant: "error",
            message: "Something Went Wrong!",
          })
        );
      }
      return response.batchId;
    } catch (error) {
      // FAILURE
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: 3000,
          variant: "error",
          message: "Something Went Wrong!",
        })
      );
      //@ts-ignore
      return error?.response?.data?.batchId;
    }
  };
};

// SEARCH FLIGHTS
export const searchFlights = (payload: IDynamicQuery, excludeRemovedFlights = false) => {
  return async (dispatch: ThunkDispatch<object, object, AnyAction>, getState: any): Promise<any> => {
    const userSession = await Auth.currentSession();
    const token = userSession.getIdToken().getJwtToken();

    try {
      const path = excludeRemovedFlights ? "/searchFlights?excludeRemovedFlights=true" : "/searchFlights";
      const response = await API.post("skytrains", path, {
        headers: {
          Accept: "*/*",
          Authorization: token,
        },
        body: payload,
      });
      const flightData = parseBasicFlights(response, getState);
      dispatch({ type: Type.TIMELINE_ADD_FLIGHT_DATA, flightData });
      return response;
    } catch (error) {
      //@ts-ignore
      return error?.response;
    }
  };
};

// SEARCH GROUND EVENTS
export const searchGroundEvents = (payload: IDynamicQuery) => {
  return async (dispatch: ThunkDispatch<object, object, AnyAction>, getState: any): Promise<any> => {
    const userSession = await Auth.currentSession();
    const token = userSession.getIdToken().getJwtToken();

    try {
      const response = await API.post("skytrains", "/searchGroundEvents", {
        headers: {
          Accept: "*/*",
          Authorization: token,
        },
        body: payload,
      });

      // NOTE: Search results are no longer injected in the redux dataset.
      // Loading takes place when the users attempt to focus on a selected search result to view it in the timeline.

      // const flightData = parseBasicFlights(response, getState);
      // dispatch({ type: Type.TIMELINE_ADD_FLIGHT_DATA, flightData });
      return response;
    } catch (error) {
      //@ts-ignore
      return error?.response;
    }
  };
};

export const getFlightById = async (flightId: string): Promise<IFullFlight | null> => {
  const userSession = await Auth.currentSession();
  const token = userSession.getIdToken().getJwtToken();

  try {
    const flight = await API.get("skytrains", `/flights/${flightId}`, {
      headers: {
        Accept: "*/*",
        Authorization: token,
      },
    });

    // NOTE: Search results are no longer injected in the redux dataset.
    // Loading takes place when the users attempt to focus on a selected search result to view it in the timeline.

    // const flightData = parseBasicFlights(response, getState);
    // dispatch({ type: Type.TIMELINE_ADD_FLIGHT_DATA, flightData });
    return flight ?? null;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const getFlightConnections = (
  windowId: string,
  flightIds: string[],
  positions?: string[]
): ThunkAction<Promise<string>, object, object, AnyAction> => {
  return async (dispatch: ThunkDispatch<object, object, AnyAction>, getState: any): Promise<any> => {
    dispatch(
      openSnackbar({
        open: true,
        closeDelay: null,
        variant: "info",
        message: "Getting Flight Connections...",
      })
    );

    const userSession = await Auth.currentSession();
    const token = userSession.getIdToken().getJwtToken();

    try {
      const response = await API.post("skytrains", "/flight/crewConnections", {
        headers: { Accept: "*/*", Authorization: token },
        body: { flightIds, positions },
      });

      if (!!response && typeof response === "object") {
        // SUCCESS
        dispatch(
          openSnackbar({
            open: true,
            closeDelay: 3000,
            variant: "success",
            message: "Flight Connections Found!",
          })
        );
      }
      dispatch(setCrewConnections(response, windowId));
      return response;
    } catch (error) {
      // FAILURE
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: 3000,
          variant: "error",
          //@ts-ignore
          message: error?.response?.data?.error ?? "Something Went Wrong!",
        })
      );
      //@ts-ignore
      return error?.response;
    }
  };
};
